/**
 * Embedded Role Provider
 *
 * Provides minimal RoleContext-compatible interface for embedded routes (Bitrix24 iframe).
 * This allows dashboard components to use useRole() hook without modification.
 *
 * Wraps the separated contexts (Auth, Organization, Permissions, etc.) with
 * minimal implementations based on EmbeddedOrgContext data.
 */
import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useEmbeddedOrg } from './EmbeddedOrgContext';
import type { GlobalRole } from '@aica/core/rbac';
import type { PermissionContextValue as FullPermissionContextValue } from './PermissionContext';
import type { AccessState } from '@/lib/route-permissions';

// Import context types from separated contexts
interface Organization {
  id: string;
  name: string;
}

/**
 * AuthContext - Minimal implementation for embedded
 */
interface AuthContextValue {
  user: { userId: string; email: string; name: string } | null;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthUser(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthUser must be used within EmbeddedRoleProvider');
  return ctx;
}

function EmbeddedAuthProvider({ children }: { children: ReactNode }) {
  const value: AuthContextValue = useMemo(
    () => ({
      user: null, // No user info in embedded mode
      isLoading: false,
      error: null,
    }),
    []
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * OrganizationContext - Minimal implementation for embedded
 */
interface OrganizationContextValue {
  organizations: Organization[];
  currentOrganization: Organization | null;
  selectedOrgId: string | null;
  selectOrganization: (orgId: string) => void;
  isLoading: boolean;
  error: string | null;
  isOrgSwitching: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

export function useOrganization(): OrganizationContextValue {
  const ctx = useContext(OrganizationContext);
  if (!ctx) throw new Error('useOrganization must be used within EmbeddedRoleProvider');
  return ctx;
}

function EmbeddedOrganizationProvider({ children }: { children: ReactNode }) {
  const { orgId, isLoading } = useEmbeddedOrg();

  const value: OrganizationContextValue = useMemo(() => {
    const org = orgId ? { id: orgId, name: 'Organization' } : null;

    return {
      organizations: org ? [org] : [],
      currentOrganization: org,
      selectedOrgId: orgId, // ✅ This is what dashboard components need!
      selectOrganization: () => {}, // No-op in embedded
      isLoading,
      error: null,
      isOrgSwitching: false,
    };
  }, [orgId, isLoading]);

  return (
    <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
  );
}

/**
 * ViewAsContext - Minimal implementation for embedded
 */
interface ViewAsContextValue {
  viewAsRole: GlobalRole | null;
  setViewAsRole: (role: GlobalRole | null) => void;
  isViewAsMode: boolean;
}

const ViewAsContext = createContext<ViewAsContextValue | null>(null);

export function useViewAs(): ViewAsContextValue {
  const ctx = useContext(ViewAsContext);
  if (!ctx) throw new Error('useViewAs must be used within EmbeddedRoleProvider');
  return ctx;
}

function EmbeddedViewAsProvider({ children }: { children: ReactNode }) {
  const value: ViewAsContextValue = useMemo(
    () => ({
      viewAsRole: null,
      setViewAsRole: () => {},
      isViewAsMode: false,
    }),
    []
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

/**
 * PermissionContext - Minimal implementation for embedded
 *
 * The shape is imported, not restated: the hand-kept copy that used to live
 * here had already drifted three fields behind the real one, and every new
 * capability silently needed a third edit nobody's compiler asked for.
 */
type PermissionContextValue = FullPermissionContextValue;

/**
 * `/access/me` is not read here (#1645): this provider sits above the consent
 * and access-request gates, and nothing in the iframe reads `access` from it.
 * What decides by it reads `useScreenSubject` (`ScreenSubjectContext`).
 * `failed` is the honest value for «not asked here»: the role decides.
 */
const ACCESS_NOT_READ_HERE: AccessState = Object.freeze({ status: 'failed' });

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function usePermissions(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be used within EmbeddedRoleProvider');
  return ctx;
}

function EmbeddedPermissionProvider({ children }: { children: ReactNode }) {
  const { role } = useEmbeddedOrg();

  const value: PermissionContextValue = useMemo(
    () => ({
      globalRole: role,
      projectRoles: new Map(),
      // The iframe never loads project roles: the map above is a placeholder.
      projectRolesLoaded: false,
      isOwner: false,
      access: ACCESS_NOT_READ_HERE,
      // Never a platform owner in the iframe, and nothing to wait for — the
      // role comes from the portal's install, not from /me.
      isOwnerResolved: true,
      // No View As inside the iframe, so the effective role is the real one.
      effectiveRole: role,
      // Allow view access by default in embedded
      canAccess: () => true,
      canManageProjects: false,
      // Project management is not exposed in the Bitrix24 iframe at all.
      canCreateProject: false,
      canSeeBilling: false,
      canManageUsers: false,
      canListUsers: false,
      canManageOrgStructure: false,
      canOperateIntegrations: false,
      canUploadRecords: false,
      canManageAlerts: false,
      canEditProject: () => false,
      canDeleteProject: () => false,
      canViewProject: () => true,
      loadProjectRole: async () => {},
    }),
    [role]
  );

  return (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
  );
}

/**
 * NavigationContext - Minimal implementation for embedded
 */
interface NavigationContextValue {
  getLandingPage: () => string;
  getVisibleScreens: () => string[];
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation must be used within EmbeddedRoleProvider');
  return ctx;
}

function EmbeddedNavigationProvider({ children }: { children: ReactNode }) {
  const value: NavigationContextValue = useMemo(
    () => ({
      getLandingPage: () => '/embedded/dashboard',
      getVisibleScreens: () => [],
    }),
    []
  );

  return (
    <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
  );
}

/**
 * Composite Embedded Role Provider
 *
 * Wraps all minimal context providers in the same order as RoleProvider.
 * This makes useRole() hook work seamlessly in embedded routes.
 */
export function EmbeddedRoleProvider({ children }: { children: ReactNode }) {
  return (
    <EmbeddedAuthProvider>
      <EmbeddedOrganizationProvider>
        <EmbeddedViewAsProvider>
          <EmbeddedPermissionProvider>
            <EmbeddedNavigationProvider>{children}</EmbeddedNavigationProvider>
          </EmbeddedPermissionProvider>
        </EmbeddedViewAsProvider>
      </EmbeddedOrganizationProvider>
    </EmbeddedAuthProvider>
  );
}
